import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
import { styled } from "@mui/material/styles";
import { Box, Button, Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { makeStyles } from "@material-ui/core";
import { pxToRem } from "../../theme/typography";
import GoogleIcon, { NewGoogleIcon } from "../../assets/images/icon/google-icon";
import FacebookIcon, { NewFacebookIcon } from "../../assets/images/icon/facebook-icon";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import palette from "../../theme/palette";
import { facebookProvider, googleProvider } from "../../config/authMethods";
import { userService } from "../../services/auth";
import SnackBarComp from "../../components/snackBar";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../config/firebase";
import { ButtonLoader } from "../../components/buttonLoader";
import { UserTypeContext } from "../../context/userStatus";
import {
  setUserId,
  setUserLevel,
  setUserStatus,
  setUserToken,
} from "../../store/actions/userTypeAction";
import FullpageLoader from "../../components/fullPageLoader";
import { UserLoginModal } from "../../interfaces/outcomeModal";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import queryParams from "../../utils/getQueryParams";
import { identifyUser, trackEvent } from "../../index";
import CommonTypo from "../../components/CommonTypo";

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup
    .string()
    .min(8, "Password should be of minimum 8 characters length")
    .required("Password is required"),
});

const RootStyle = styled("div")(({ theme }) => ({
  justifyContent: "center",
  display: "flex",
  flexDirection: "column",
  gap: "22px",
  padding: "0 100px",
  minWidth: "50%",
  [theme.breakpoints.up("xl")]: {
    padding: "60px",
  },
  [theme.breakpoints.down("lg")]: {
    padding: "40px",
  },
  [theme.breakpoints.down("md")]: {
    padding: "30px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "20px",
  },
}));
const RootStyle2 = styled("div")(({ theme }) => ({
  padding: "0",

}));

const useStyles = makeStyles((theme) => ({
  registrationHeading: {
    color: palette.primary.light,
    marginBottom: "62px!important",
    [theme.breakpoints.down("lg")]: {
      marginBottom: "50px!important",
    },
    [theme.breakpoints.down("md")]: {
      marginBottom: "40px!important",
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: "30px!important",
    },
  },
  socialLoginButton: {
    marginTop: pxToRem(24),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "24px"
  },
  socialLoginButtonWeb: {
    marginTop: pxToRem(24),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "24px"
  },
  alreadyAccountText: {
    marginTop: pxToRem(60),
    color: "#9BA19D",
    fontSize: pxToRem(16),
    lineHeight: pxToRem(16),
    fontWeight: 400,
    textAlign: "center",
  },
  alreadyAccountTextMobile: {
    marginTop: pxToRem(30),
    color: "#9BA19D",
    fontSize: pxToRem(16),
    lineHeight: pxToRem(16),
    fontWeight: 400,
    textAlign: "center",
  },
  forgetPasswordText: {
    color: "#9BA19D",
    textAlign: "right",
    fontSize: pxToRem(14),
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "145%", /* 20.3px */
    letterSpacing: "-0.14px",
    cursor: "pointer"
  },
  alreadyAccountTextSpan: {
    color: "#11734A",
    cursor: "pointer",
  },
  welcomeTitle: {
    fontSize: "26px !important",
    lineHeight: "120% !important"
  },
  welcomeSubTitle: {
    fontSize: "16px !important",
    lineHeight: "100% !important"
  },
  orLink: {
    marginTop: `${pxToRem(70)} !important`,
  },
  textInputMobile: {
    "& .MuiOutlinedInput-root": {
      border: "1px solid #B8E29E",
      '& .MuiInputBase-input': {
        '&::placeholder': {
          fontSize: `${pxToRem(14)} !important`,
          color: '#3E4240',
          opacity: 1, /* Firefox */
          lineHeight: '100%',
          fontWeight: 400,
        }
      },
    },

  }
}));

const ariaLabel = { "aria-label": "description" };

const RightPartLogin = ({ isMobile = false }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const queryData = queryParams();
  const [showSnakbar, setShowSnakbar] = useState(false);
  const [snakbarMsg, setSnakbarMsg] = useState("");
  const [msgType, setMsgType] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [showLoaderPage, setShowLoaderPage] = useState(false);
  const { dispatch: userTypeDispatch } = useContext(UserTypeContext);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    errors: {
      email: "",
      password: ""
    }
  });

  useEffect(() => {
    userTypeDispatch(setUserId(undefined));
  }, [userTypeDispatch]);

  const validateForm = useCallback(() => {
    const errors = {
      email: "",
      password: ""
    };
    let isValid = true;

    if (!formData.email) {
      errors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Enter a valid email";
      isValid = false;
    }

    if (!formData.password) {
      errors.password = "Password is required";
      isValid = false;
    } else if (formData.password.length < 8) {
      errors.password = "Password should be of minimum 8 characters length";
      isValid = false;
    }

    setFormData(prev => ({
      ...prev,
      errors
    }));

    return isValid;
  }, [formData.email, formData.password]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      errors: {
        ...prev.errors,
        [name]: ""
      }
    }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setShowLoader(true);
    try {
      const result = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      if (result?.user) {
        localStorage.setItem("userDetails", JSON.stringify(result));
        await userContinueLogin(result?.user?.uid);
      }
    } catch (err: any) {
      setShowLoader(false);
      setShowSnakbar(true);
      setMsgType("error");
      if (err.code === "auth/user-not-found") {
        setSnakbarMsg("User not found");
      } else if (err.code === "auth/wrong-password") {
        setSnakbarMsg("Please enter right credential");
      } else {
        setSnakbarMsg(err.message);
      }
    }
  }, [formData.email, formData.password, validateForm]);

  const userContinueLogin = useCallback(async (uid: string) => {
    const UID = { uid };
    try {
      const data: UserLoginModal = await userService.userLogin(UID.uid);
      localStorage.setItem("userId", UID.uid);
      setMsgType("success");
      setShowSnakbar(true);
      setSnakbarMsg(data.message);
      setShowLoaderPage(false);
      setShowLoader(false);
      localStorage.setItem("userDetails", data["x-access_token"]);
      localStorage.setItem("userValues", JSON.stringify(data));
      userTypeDispatch(setUserToken(data["x-access_token"]));
      userTypeDispatch(setUserLevel(data.level));
      userTypeDispatch(setUserId(uid));
      console.log("data login", data);
      userService
        .getUserById({ uid: UID.uid })
        .then((userDetails: any) => {
          // Track successful login in Mixpanel
          identifyUser(uid, {
            name: userDetails?.data?.first_name + ' ' + userDetails?.data?.last_name,
            email: userDetails?.data?.user_email,
            user_level: data?.level,
            signup_type: userDetails?.data?.signUpType
          });

          trackEvent('User Logged In', {
            user_id: uid,
            user_level: data?.level,
            signup_type: userDetails?.data?.signUpType,
            login_method: 'email'
          });
        }).catch((err: any) => {
          console.log("err login", err);
        });


      if (data?.level === "NOT_FOUND") {
        navigate("/user-identification");
      } else {
        setTimeout(() => {
          if (data?.level === "LOW" && (!data?.outcome || !data?.influence)) {
            navigate("/plan-create");
          } else if (
            (data?.level === "HIGH" && !data?.outcome) ||
            !data?.influence ||
            !data?.reflective
          ) {
            navigate("/plan-create");
          } else {
            navigate("/tracking");
            userTypeDispatch(setUserStatus(false));
          }
        }, 2000);
      }
    } catch (error: any) {
      // Track failed login attempt
      trackEvent('Login Failed', {
        error: error.message,
        login_method: 'email'
      });
      if (error.response?.data?.description === "User is not registered") {
        // const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
        // const userData = {
        //   uid: userDetails?.user?.uid || userDetails?.uid,
        //   user_email: userDetails?.user?.email || userDetails?.email,
        //   signUpType: userDetails?.providerData?.[0]?.providerId === "google.com" ? "GMAIL" :
        //     userDetails?.providerData?.[0]?.providerId === "facebook.com" ? "FACEBOOK" : "OTHER",
        //   first_name: userDetails?.displayName?.split(" ")[0],
        //   last_name: userDetails?.displayName?.split(" ")[1],
        //   ipAddress: localStorage.getItem("ipAddress")
        // };
        // const res = await userService.createUser(userData);
        // localStorage.setItem("userValues", JSON.stringify(res.data));
        // localStorage.setItem("userDetails", res["x-access_token"]);
        // userTypeDispatch(setUserToken(res["x-access_token"]));
        // if (res?.data?.level === "NOT_FOUND") {
        //   navigate("/user-identification");
        // } else {
        //   navigate("/outcome");
        // }
        navigate("/onboarding")
      }
      setShowLoaderPage(false);
      setShowLoader(false);
      setMsgType("error");
      setShowSnakbar(true);
      setSnakbarMsg(error.message);
    }
  }, [navigate, userTypeDispatch]);

  const handleOnClick = useCallback(async (provider: any) => {
    setShowLoaderPage(true);
    try {
      const result = await userService.socialMediaAuth(provider);
      if (!result.uid) {
        throw new Error(result.message);
      }
      // localStorage.setItem("userDetails", JSON.stringify(result));

      // Track social login attempt
      trackEvent('Social Login Attempt', {
        provider: provider.providerId,
        success: true
      });
      await userContinueLogin(result?.uid);
    } catch (error: any) {
      // Track failed social login
      trackEvent('Social Login Failed', {
        provider: provider.providerId,
        error: error.message
      });

      setShowLoaderPage(false);
      setShowLoader(false);
      setMsgType("error");
      setShowSnakbar(!error?.message?.includes("auth/popup-closed-by-user"));
      if (error.message === "Firebase: Error (auth/account-exists-with-different-credential).") {
        setSnakbarMsg("Account already exists with diffrent credential");
      } else if (!error?.message?.includes("auth/popup-closed-by-user")) {
        setSnakbarMsg(error?.message);
      }
    }
  }, [userContinueLogin]);

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const handleMouseDownPassword = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  const handleCloseSnakbar = useCallback(() => {
    setShowSnakbar(false);
  }, []);

  const Root = useMemo(() => isMobile ? RootStyle2 : RootStyle, [isMobile]);

  return (
    <Root className={isMobile ? "" : "login-right-part"}>
      {showLoaderPage && <FullpageLoader />}
      <SnackBarComp
        showSnakbar={showSnakbar}
        handleCloseSnakbar={handleCloseSnakbar}
        snakbarMsg={snakbarMsg}
        type={msgType || "info"}
      />
      {!isMobile && <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <img src="/loginbear.png" style={{
          width: "218px",
          height: "218px",
        }} alt="login"></img>
      </div>}
      {isMobile ? (
        <Box display={"flex"} alignItems={"center"} justifyContent={"center"} flexDirection={"column"} width={"100%"} padding={"70px 0 60px"} gap={"8px"}>
          <Typography component={"span"} variant="h1" className={classes.welcomeTitle} fontWeight={"700 !important"} color={palette.primary.light}>Welcome back</Typography>
          <Typography component={"span"} variant="h1" className={classes.welcomeSubTitle} fontWeight={"400 !important"} color={palette.primary.light}>Log in to Tenacious Tracker</Typography>
        </Box>
      ) : (
        <Box display={"flex"} alignItems={"center"} justifyContent={"center"} flexDirection={"column"} width={"100%"} padding={"0px"} gap={"8px"}>
          <CommonTypo size={32} fontWeight={700} color="var(--color-text-primary)" lineHeight={36}>Welcome back</CommonTypo>
          <CommonTypo size={16} fontWeight={400} color="var(--color-text-secondary)" lineHeight={36}>Don’t have an account yet? <Link to={"/onboarding"} style={{
            textDecoration: "none",
            color: "#367DEF"
          }}>
            <span style={{
              textDecoration: "none",
              color: "#367DEF"
            }}>Sign up</span>
          </Link></CommonTypo>
          {/* <Typography component={"span"} variant="h1" className={classes.welcomeSubTitle} fontWeight={"400 !important"} color={palette.primary.light}>Log in to Tenacious Tracker</Typography> */}
        </Box>
      )}

      <Grid style={isMobile ? {} : {
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        gap: "16px"
      }}>
        <form onSubmit={handleSubmit} style={isMobile ? {} : {
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          alignItems: "center",
          width: "100%"
        }}>
          <TextField
            placeholder="Email"
            variant="outlined"
            fullWidth
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            error={Boolean(formData.errors.email)}
            helperText={formData.errors.email}
            className={isMobile ? classes.textInputMobile : "newInputBoxv2"}
          />
          <TextField
            placeholder="Password"
            variant="outlined"
            fullWidth
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            error={Boolean(formData.errors.password)}
            helperText={formData.errors.password}
            className={isMobile ? classes.textInputMobile : "newInputBoxv2"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {/* !isMobile */false && (
            <Box display="flex" alignItems="flex-start" gap="12px" alignSelf="stretch">
              <Box display="flex" justifyContent={"flex-end"} alignItems="center" gap="4px" flex={"1 0 0"}>
                <p className={classes.forgetPasswordText} onClick={() => navigate("/forget-password")}>
                  Forgot password?
                </p>
              </Box>
            </Box>
          )}
          {!showLoader ? (
            <Button
              type="submit"
              variant="contained"
              className="new-button"
              fullWidth
              sx={isMobile ? { mt: pxToRem(isMobile ? 20 : 0), mb: isMobile ? `${pxToRem(0)} !important` : pxToRem(0) } : {
                borderRadius: '20px !important',
                height: "40px",
                color: "#FFF !important",
                mb: `${pxToRem(22)} !important`
              }}
              disabled={!formData.email || !formData.password}
            >
              {isMobile ? "Log in" : "Sign in"}
            </Button>
          ) : (
            <Button
              variant="contained"
              className="new-button"
              fullWidth
              sx={isMobile ? { mt: pxToRem(isMobile ? 20 : 0), mb: isMobile ? `${pxToRem(0)} !important` : pxToRem(0) } : {
                borderRadius: '20px !important',
                height: "40px",
                color: "#FFF !important",
                mb: `${pxToRem(22)} !important`
              }}
            >
              <ButtonLoader />
            </Button>
          )}
        </form>
        {isMobile && (
          <Box display="flex" alignItems="center" gap="12px" alignSelf="stretch" sx={{ mt: pxToRem(24) }}>
            <Box display="flex" justifyContent={"center"} alignItems="center" gap="4px" flex={"1 0 0"}>
              <p className={classes.forgetPasswordText} onClick={() => navigate("/forget-password")}>
                Forgot password?
              </p>
            </Box>
          </Box>
        )}
        <p className={`${isMobile ? "registation_text" : "registation_textv2"} ${isMobile ? classes.orLink : ""}`}>
          <span>{isMobile ? "or continue with" : "or"}</span>
        </p>
        <Grid className={isMobile ? classes.socialLoginButton : classes.socialLoginButtonWeb}>
          <Button
            sx={isMobile ? { padding: 0 } : {
              width: "130px",
              height: "48px",
              gap: "8px",
              borderRadius: "12px",
              border: "1px solid var(--color-border)",
              padding: "0px",
            }}
            onClick={() => handleOnClick(googleProvider)}
          >
            <NewGoogleIcon isMobile={isMobile} />
          </Button>
        </Grid>
        {isMobile && <p className={isMobile ? classes.alreadyAccountTextMobile : classes.alreadyAccountText}>
          You don't have an account?{" "}
          <span
            className={classes.alreadyAccountTextSpan}
            onClick={() => navigate((isMobile ? "/register" : "/onboarding") + queryData.qeryString)}
          >
            Sign up
          </span>
        </p>}
      </Grid>
    </Root>
  );
};

export default React.memo(RightPartLogin);
